import Decimal from 'decimal.js';

Decimal.set({ precision: 30, rounding: Decimal.ROUND_HALF_UP });

export const ZERO = new Decimal(0);

export function money(value: Decimal.Value): Decimal {
  return new Decimal(value);
}

export function sum(values: Decimal[]): Decimal {
  return values.reduce((acc, v) => acc.plus(v), ZERO);
}

export function pct(amount: Decimal, percent: Decimal.Value): Decimal {
  return amount.mul(percent).div(100);
}

export function isPositive(amount: Decimal): boolean {
  return amount.gt(0);
}

export function format(amount: Decimal, currency: string, locale = 'en-PH'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount.toNumber());
}

/**
 * Format a monetary amount for display without a currency symbol. The currency
 * code is rendered separately by `<Money>` so callers can place it on either
 * side of the number, drop it in implicit-currency contexts, or restyle it.
 *
 * Accepts string | number | Decimal. Strings are the API's canonical money
 * shape (Decimal-safe) and must NOT round-trip through Number() per the
 * project's money rules; we route through Decimal first.
 */
export function formatAmount(
  amount: string | number | Decimal,
  opts: { locale?: string; minFractionDigits?: number; maxFractionDigits?: number } = {},
): string {
  const { locale = 'en-PH', minFractionDigits = 2, maxFractionDigits = 2 } = opts;
  const d = amount instanceof Decimal ? amount : new Decimal(amount);
  return new Intl.NumberFormat(locale, {
    style: 'decimal',
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits,
  }).format(d.toNumber());
}

export { Decimal };
